import { useState } from "react"
import {
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Download,
    ChevronDown,
    ChevronUp,
    Upload
} from "lucide-react"

type ContactItem = {
    email: string
    reason?: string
    error?: string
}

export type ImportResultType = {
    success: boolean,
    summary: {
        total: number
        imported: number
        updated: number
        skipped: number
        errors: number
    }
    skippedContacts?: ContactItem[]
    errors?: ContactItem[]
}

function StatCard({ label, value, icon }: any) {
    return (
        <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-1">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
                {label}
                {icon}
            </div>

            <div className="text-2xl font-semibold">{value}</div>
        </div>
    )
}

export default function ImportResult({
    result,
    onBack
}: {
    result?: ImportResultType
    onBack?: () => void
}) {
    const [showSkipped, setShowSkipped] = useState(true)
    const [showErrors, setShowErrors] = useState(true)

    if (!result) return null

    const { summary, skippedContacts = [], errors = [] } = result

    function downloadCSV(data: ContactItem[]) {
        const rows = [
            ["Email", "Reason"],
            ...data.map((c) => [c.email, c.reason]),
        ]

        const csv = rows.map((r) => r.join(",")).join("\n")

        const blob = new Blob([csv], { type: "text/csv" })
        const url = URL.createObjectURL(blob)

        const a = document.createElement("a")
        a.href = url
        a.download = "skipped-contacts.csv"
        a.click()
    }

    return (
        <div className="space-y-6">

            {/* Header */}
            <div className="flex items-center gap-3 text-green-500">
                <CheckCircle2 size={26} />
                <div>
                    <h3 className="text-lg font-semibold">Import Completed</h3>
                    <p className="text-sm text-muted-foreground">
                        Your contacts have been processed successfully.
                    </p>
                </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-5 gap-4">

                <StatCard label="Total" value={summary.total} />

                <StatCard
                    label="Imported"
                    value={summary.imported}
                    icon={<CheckCircle2 className="text-green-500" size={18} />}
                />

                <StatCard
                    label="Updated"
                    value={summary.updated}
                    icon={<CheckCircle2 className="text-blue-500" size={18} />}
                />

                <StatCard
                    label="Skipped"
                    value={summary.skipped}
                    icon={<AlertTriangle className="text-yellow-500" size={18} />}
                />

                <StatCard
                    label="Errors"
                    value={summary.errors}
                    icon={<XCircle className="text-red-500" size={18} />}
                />

            </div>

            {/* Skipped Contacts */}
            {skippedContacts.length > 0 && (
                <div className="rounded-lg border border-border bg-muted/20">

                    <div className="flex items-center justify-between p-4 border-b border-border">

                        <div className="flex items-center gap-2 font-medium">
                            <AlertTriangle className="text-yellow-500" size={18} />
                            Skipped Contacts ({skippedContacts.length})
                        </div>

                        <div className="flex items-center gap-4">

                            <button
                                onClick={() => downloadCSV(skippedContacts)}
                                className="flex items-center gap-1 text-sm text-primary hover:underline"
                            >
                                <Download size={16} />
                                Download CSV
                            </button>

                            <button
                                onClick={() => setShowSkipped(!showSkipped)}
                                className="flex items-center gap-1 text-sm"
                            >
                                {showSkipped ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                {showSkipped ? "Hide" : "View"}
                            </button>

                        </div>

                    </div>

                    {showSkipped && (
                        <div className="max-h-56 overflow-auto divide-y divide-border">

                            {skippedContacts.map((c, i) => (
                                <div
                                    key={i}
                                    className="flex justify-between px-4 py-2 text-sm"
                                >
                                    <span className="font-medium">{c.email}</span>
                                    <span className="text-muted-foreground">{c.reason}</span>
                                </div>
                            ))}

                        </div>
                    )}

                </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
                <div className="rounded-lg border border-border bg-red-500/5">

                    <div className="flex items-center justify-between p-4 border-b border-border font-medium">

                        <div className="flex items-center gap-2">
                            <XCircle className="text-red-500" size={18} />
                            Import Errors ({errors.length})
                        </div>

                        <button
                            onClick={() => setShowErrors(!showErrors)}
                            className="flex items-center gap-1 text-sm"
                        >
                            {showErrors ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            {showErrors ? "Hide" : "View"}
                        </button>

                    </div>

                    {showErrors && (
                        <div className="max-h-56 overflow-auto divide-y divide-border">

                            {errors.map((e, i) => (
                                <div
                                    key={i}
                                    className="flex justify-between px-4 py-2 text-sm"
                                >
                                    <span>{e.email}</span>
                                    <span className="text-red-500">{e.error}</span>
                                </div>
                            ))}

                        </div>
                    )}

                </div>
            )}

            {/* Footer */}
            {onBack && (
                <div className="flex justify-end pt-2">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-border hover:bg-muted transition"
                    >
                        <Upload size={16} />
                        Go Back to Uploader
                    </button>
                </div>
            )}

        </div>
    )
}